import React, { useState, useEffect, useRef } from 'react';
import {
  ShoppingCart, Plus, Minus, Search,
  Coffee, Utensils, Cookie, X, Check,
  Phone, User, Trash2, ChevronDown, Store, Package,
  ChevronUp, Receipt
} from 'lucide-react';
import { host, TAXES,TAX_TYPE_ENUM,resolveTaxType,computeTaxBreakdown } from '../script/variables';
import '../style/TakeOrder.css';

import { GradientPlaceholder } from './GradientPlaceholder';


/* ─── helpers ─────────────────────────────────────────────── */
const cap = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

const calcPrice = (qty, price, disc) =>
  Math.ceil(qty * price * (1 - disc / 100) * 100) / 100;

/* ─── DYNAMIC TAX ENGINE ──────────────────────────────────────
   Mirrors MenuItems.jsx exactly: reads the TAXES config and
   produces a line-by-line breakdown, e.g.
     grouped: { GST: { SGST: 2.5, CGST: 2.5 } }
     flat:    { OTHER: 0 }
   Any key outside the schema enum is bucketed as "OTHER".
──────────────────────────────────────────────────────────── */


// ── Totals: dynamic tax applied only to taxable items ───────
// item.isTaxable === false  → MRP, price already includes all tax
// item.isTaxable !== false  → standard, TAXES config applied on top
const calcTotals = (cart) => {
  let taxableSub    = 0;
  let nonTaxableSub = 0;
  let fullTaxable   = 0;
  let fullNonTax    = 0;

  for (const k in cart) {
    const { quantity, price, discount, isTaxable } = cart[k];
    const lineNet = quantity * price * (1 - discount / 100);
    if (isTaxable === false) {
      nonTaxableSub += lineNet;
      fullNonTax    += quantity * price;
    } else {
      taxableSub  += lineNet;
      fullTaxable += quantity * price;
    }
  }

  taxableSub    = Math.round(taxableSub    * 100) / 100;
  nonTaxableSub = Math.round(nonTaxableSub * 100) / 100;
  const sub     = Math.round((taxableSub + nonTaxableSub) * 100) / 100;

  // Per-line breakdown driven by TAXES, each line rounded independently,
  // total tax = sum of those rounded lines (matches the backend record).
  const taxBreakdown   = computeTaxBreakdown(taxableSub);
  const totalTaxAmount = Math.round(taxBreakdown.reduce((s, t) => s + t.amount, 0) * 100) / 100;

  const total = Math.ceil(sub + totalTaxAmount);

  // "You save" compares against full price + the same effective tax rate.
  const effectiveTaxRate = taxableSub > 0 ? totalTaxAmount / taxableSub : 0;
  const fullWithTax = Math.ceil(fullTaxable * (1 + effectiveTaxRate) + fullNonTax);
  const saved = Math.max(0, fullWithTax - total);

  return {
    taxableSub,
    nonTaxableSub,
    sub,
    taxBreakdown,
    totalTaxAmount,
    total,
    saved,
    hasMixed:      taxableSub > 0 && nonTaxableSub > 0,
    hasNonTaxable: nonTaxableSub > 0,
  };
};

const catIcon = (type) => {
  switch (type) {
    case 'breakfast': return <Coffee  size={13} />;
    case 'lunch':
    case 'dinner':    return <Utensils size={13} />;
    default:          return <Cookie  size={13} />;
  }
};

const vegDot = (type) => (
  <span className={`to-veg to-veg-${type}`}>
    {type === 'veg' ? '●' : type === 'nonveg' ? '●' : '●'}
  </span>
);




/* ─── DishCard ────────────────────────────────────────────── */
const DishCard = ({ dish, menuType, onAdd }) => (
  <div className={`to-dish ${dish.isAvailable === false ? 'to-dish-oos' : ''}`}>
    {dish.isAvailable === false && (
      <div className="to-oos-overlay">
        <span className="to-oos-label">Out of Stock</span>
      </div>
    )}
    <div className="to-dish-img-box">
   
    {
    (dish.image && dish.image.trim() && dish.image !== 'null' && dish.image !== 'undefined')
    ? <img src={dish.image} alt={dish.name} className="to-dish-img" loading="lazy" />
    : <GradientPlaceholder name={dish.name} />
  }
      {vegDot(dish.type)}
    </div>
    <div className="to-dish-body">
      <p className="to-dish-name">
        {dish.name}
        {/* MRP badge on card — no tax will be added at checkout */}
        {dish.isTaxable === false && (
          <span className="to-mrp-badge"> MRP</span>
        )}
      </p>
      <div className="to-sizes">
        {['small', 'medium', 'large'].map(sz =>
          dish[sz]?.price > 0 && (
            <button
              key={sz}
              className="to-sz-btn"
              onClick={() => dish.isAvailable !== false && onAdd(dish, sz, menuType)}
              disabled={dish.isAvailable === false}
            >
              <span className="to-sz-ltr">{sz[0].toUpperCase()}</span>
              <span className="to-sz-price">₹{dish[sz].price}</span>
              {dish[sz].discount > 0 && (
                <span className="to-sz-badge">{dish[sz].discount}%</span>
              )}
            </button>
          )
        )}
      </div>
    </div>
  </div>
);

/* ─── CartRow ─────────────────────────────────────────────── */
const CartRow = ({ item, onInc, onDec, onRemove }) => (
  <div className="to-cart-row">
    <img src={item.image} alt={item.name} className="to-cart-thumb" />
    <div className="to-cart-info">
      <p className="to-cart-name">{item.name}</p>
      <span className="to-cart-size">
        {cap(item.pack)}
        {/* Inline MRP note in cart row */}
        {item.isTaxable === false && (
          <span className="to-mrp-tag"> · MRP</span>
        )}
      </span>
    </div>
    <div className="to-qty">
      <button onClick={() => onDec(item.id, item.pack)}><Minus size={11} /></button>
      <span>{item.quantity}</span>
      <button onClick={() => onInc(item.id, item.pack)}><Plus size={11} /></button>
    </div>
    <span className="to-cart-sub">₹{calcPrice(item.quantity, item.price, item.discount)}</span>
    <button className="to-cart-del" onClick={() => onRemove(item.id, item.pack)}>
      <Trash2 size={13} />
    </button>
  </div>
);

/* ─── CustomerSuggestions ─────────────────────────────────── */
const CustomerSuggestions = ({ items, loading, onPick }) => {
  if (!loading && !items.length) return null;
  return (
    <div className="to-cust-suggest">
      {loading ? (
        <div className="to-cust-suggest-loading">
          <div className="to-table-spin" />
          <span>Searching…</span>
        </div>
      ) : (
        items.map((c, i) => (
          <button
            type="button"
            key={`${c.customerPhone}-${i}`}
            className="to-cust-suggest-item"
            onMouseDown={(e) => e.preventDefault()} // keep input focus/click alive before blur fires
            onClick={() => onPick(c)}
          >
            <User size={12} className="to-cust-suggest-icon" />
            <span className="to-cust-suggest-name">{c.customerName || 'Guest'}</span>
            <span className="to-cust-suggest-phone">{c.customerPhone}</span>
          </button>
        ))
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ */
const TakeOrder = ({ cafeid, socket }) => {
  const [menuData,    setMenuData]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [cart,        setCart]        = useState({});
  const [cartSize,    setCartSize]    = useState(0);
  const [search,      setSearch]      = useState('');
  const [category,    setCategory]    = useState('all');
  const [custName,    setCustName]    = useState('');
  const [custPhone,   setCustPhone]   = useState('');
  const [note,        setNote]        = useState('');
  const [placing,     setPlacing]     = useState(false);
  const [showCart,    setShowCart]    = useState(false);
  const [success,     setSuccess]     = useState(null);

  const [orderType,     setOrderType]     = useState('takeaway');
  const [tables,        setTables]        = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [loadingTables, setLoadingTables] = useState(false);

  const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);

  /* ── customer lookup-by-phone state ── */
  const [custSuggestions,      setCustSuggestions]      = useState([]);
  const [showCustSuggestions,  setShowCustSuggestions]  = useState(false);
  const [searchingCustomer,    setSearchingCustomer]    = useState(false);
  const custJustPickedRef = useRef(false); // avoid re-searching right after a pick

  /* ── socket ── */
  useEffect(() => {
    if (!socket) return;
    socket.emit('join-room', cafeid);
    socket.on('connect', () => {});
    socket.on('recieve-payment-status', () => {});
    socket.on('receive-message', () => {});
    socket.on('welcome', () => {});
    return () => {
      socket.off('connect');
      socket.off('recieve-payment-status');
      socket.off('receive-message');
      socket.off('welcome');
    };
  }, [socket, cafeid]);

  /* ── fetch menu ── */
  useEffect(() => { fetchMenu(); }, [cafeid]);

  const fetchMenu = async () => {
    try {
      setLoading(true); setError(null);
      const r = await fetch(`${host}/order/fetchMenu?cafeid=${cafeid}`, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (r.ok) setMenuData(await r.json());
      else setError('Failed to load menu');
    } catch { setError('Network error — please retry'); }
    finally   { setLoading(false); }
  };

  /* ── fetch tables ── */
  const fetchTables = async () => {
    setLoadingTables(true);
    try {
      const token = localStorage.getItem('partnertoken');
      const r = await fetch(`${host}/tables/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ restaurantId: cafeid })
      });
      const data = await r.json();
      if (data.success) {
        setTables(data.data.filter(t => t.status === 'available' && t.isActive));
      }
    } catch (err) {
      console.error('Error fetching tables:', err);
    } finally {
      setLoadingTables(false);
    }
  };

  useEffect(() => {
    if (orderType === 'dineIn') fetchTables();
    else { setTables([]); setSelectedTable(null); }
  }, [orderType]);

  /* ── customer-by-phone lookup, debounced ──────────────────
     Fires once the user has typed at least 3 digits. Skips
     the search right after a suggestion was clicked (so the
     dropdown doesn't immediately reopen with the same phone).
  ─────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (custJustPickedRef.current) {
      custJustPickedRef.current = false;
      return;
    }

    const digits = custPhone.replace(/\D/g, '');

    if (digits.length < 3) {
      setCustSuggestions([]);
      setShowCustSuggestions(false);
      setSearchingCustomer(false);
      return;
    }
    const token = localStorage.getItem('partnertoken');

    setSearchingCustomer(true);
    const t = setTimeout(async () => {
      try {

        const r = await fetch(`${host}/partner/customerdetails?phone=${digits}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token }
      });


        
        const data = await r.json();
        if (data.success) {
          setCustSuggestions(data.data || []);
          setShowCustSuggestions((data.data || []).length > 0);
        } else {
          setCustSuggestions([]);
          setShowCustSuggestions(false);
        }
      } catch (err) {
        console.error('Error searching customer:', err);
        setCustSuggestions([]);
        setShowCustSuggestions(false);
      } finally {
        setSearchingCustomer(false);
      }
    }, 350); // debounce

    return () => clearTimeout(t);
  }, [custPhone, cafeid]);

  const pickCustomerSuggestion = (c) => {
    custJustPickedRef.current = true;
    setCustName(c.customerName || '');
    setCustPhone(c.customerPhone || '');
    setShowCustSuggestions(false);
    setCustSuggestions([]);
  };

  /* ── cart helpers ──────────────────────────────────────────
     isTaxable is copied from the dish into the cart item here.
     dish.isTaxable === false  → MRP, no tax at checkout.
     undefined / true          → taxable (backward compat).
  ─────────────────────────────────────────────────────────── */
  const addToCart = (dish, sz, menuType) => {
    const key = `${dish._id}-${sz}`;
    setCart(prev => {
      if (prev[key]) {
        if (prev[key].quantity >= 20) return prev;
        return { ...prev, [key]: { ...prev[key], quantity: prev[key].quantity + 1 } };
      }
      setCartSize(p => p + 1);
      return {
        ...prev,
        [key]: {
          id:        dish._id,
          name:      dish.name,
          pack:      sz,
          price:     dish[sz].price,
          discount:  dish[sz].discount,
          image:     dish.image,
          type:      dish.type,
          menuType,
          quantity:  1,
          isTaxable: dish.isTaxable !== false,   // ← carry taxability into cart,
          dishId: dish._id
        }
      };
    });
  };

  const inc = (id, pack) => {
    const key = `${id}-${pack}`;
    setCart(prev => {
      if (!prev[key] || prev[key].quantity >= 20) return prev;
      return { ...prev, [key]: { ...prev[key], quantity: prev[key].quantity + 1 } };
    });
  };

  const dec = (id, pack) => {
    const key = `${id}-${pack}`;
    setCart(prev => {
      if (!prev[key]) return prev;
      if (prev[key].quantity > 1)
        return { ...prev, [key]: { ...prev[key], quantity: prev[key].quantity - 1 } };
      const n = { ...prev }; delete n[key];
      setCartSize(p => p - 1);
      return n;
    });
  };

  const remove = (id, pack) => {
    const key = `${id}-${pack}`;
    setCart(prev => { const n = { ...prev }; delete n[key]; return n; });
    setCartSize(p => p - 1);
  };

  /* ── place order ── */
const placeOrder = async () => {
  if (!custName.trim())                          return alert('Customer name is required');
  if (!custPhone.trim() || custPhone.length < 10) return alert('Enter valid 10-digit phone');
  if (!Object.keys(cart).length)                 return alert('Cart is empty');
  if (orderType === 'dineIn' && !selectedTable)  return alert('Please select a table for dine-in');

  setPlacing(true);
  try {
    const token = localStorage.getItem('partnertoken');

    // Only send WHAT was picked — dishId, size, quantity. Price, discount,
    // and tax are recomputed server-side from the live menu, same as the
    // customer-facing QR flow.
    const items = Object.values(cart).map(item => ({
      dishId: item.dishId,
      size: item.pack,
      quantity: item.quantity,
    }));

    const payload = {
      items,
      cafeid,
      instruction: note,
      customerName: custName,
      customerPhone: custPhone,
      orderType: orderType === 'dineIn' ? 'dine-in' : 'takeaway'
    };
    if (orderType === 'dineIn' && selectedTable) {
      payload.tableId     = selectedTable._id;
      payload.tableNumber = selectedTable.tableNumber;
      payload.areaId      = selectedTable.areaId?._id || null;
      payload.areaName    = selectedTable.areaId?.name || 'General';
    }
    const r = await fetch(`${host}/order/placeManualOrder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify(payload)
    });
    const data = await r.json();

    if (r.ok) {
      if (socket) socket.emit('message', { message: data, room: cafeid });
      setSuccess({ id: data._id, total: calcTotals(cart).total });
      setCart({}); setCartSize(0);
      setCustName(''); setCustPhone(''); setNote('');
      setSelectedTable(null);
      setShowTaxBreakdown(false);
      setCustSuggestions([]); setShowCustSuggestions(false);
      setTimeout(() => { setSuccess(null); setShowCart(false); }, 3500);
    } else alert(data.msg || 'Failed to place order');
  } catch { alert('Network error. Please retry.'); }
  finally  { setPlacing(false); }
};

  /* ── filtered menu ── */
  const filtered = menuData?.menu
    ?.filter(s => category === 'all' || s.type === category)
    .map(s => ({
      ...s,
      categories: s.categories
        ?.map(c => ({
          ...c,
          dishes: c.dishes?.filter(d =>
            d.name.toLowerCase().includes(search.toLowerCase())
          )
        }))
        .filter(c => c.dishes?.length > 0)
    }))
    .filter(s => s.categories?.length > 0);

  const cats    = ['all', ...new Set(menuData?.menu?.map(m => m.type) || [])];
  const totals  = calcTotals(cart);
  const hasCart = Object.keys(cart).length > 0;

  /* ── loading / error ── */
  if (loading) return (
    <div className="to-shell to-center">
      <div className="to-spinner" />
      <span className="to-hint">Loading menu…</span>
    </div>
  );

  if (error) return (
    <div className="to-shell to-center">
      <p className="to-err-msg">{error}</p>
      <button className="to-retry" onClick={fetchMenu}>↺ Retry</button>
    </div>
  );

  return (
    <div className="to-shell">

      {/* ══════ MENU SIDE ══════ */}
      <div className="to-menu-side">

        <div className="to-topbar">
          <div className="to-topbar-left">
            <h1 className="to-title">{menuData?.name || 'Menu'}</h1>
            <span className="to-badge-pos">Counter POS</span>
          </div>
          <button
            className="to-mob-trigger"
            onClick={() => setShowCart(true)}
            aria-label="Open cart"
          >
            <ShoppingCart size={19} />
            {cartSize > 0 && <span className="to-mob-badge">{cartSize}</span>}
          </button>
        </div>

        <div className="to-controls">
          <div className="to-search-box">
            <Search size={15} className="to-s-icon" />
            <input
              className="to-search-in"
              placeholder="Search dishes…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="to-s-clear" onClick={() => setSearch('')}>
                <X size={13} />
              </button>
            )}
          </div>

          <div className="to-cats">
            {cats.map(c => (
              <button
                key={c}
                className={`to-cat ${category === c ? 'to-cat-on' : ''}`}
                onClick={() => setCategory(c)}
              >
                {c !== 'all' && catIcon(c)}
                {c === 'all' ? 'All' : cap(c)}
              </button>
            ))}
          </div>
        </div>

        <div className="to-scroll">
          {!filtered?.length && (
            <div className="to-empty-menu">
              <Search size={28} />
              <p>No dishes found</p>
            </div>
          )}
          {filtered?.map((sec, si) => (
            <div key={si} className="to-sec">
              <div className="to-sec-label">
                {catIcon(sec.type)}
                <span>{cap(sec.type)}</span>
              </div>
              {sec.categories?.map((cat, ci) => (
                <div key={ci} className="to-subcat">
                  <h3 className="to-subcat-h">{cat.name}</h3>
                  <div className="to-grid">
                    {cat.dishes?.map((dish, di) => (
                      <DishCard
                        key={di}
                        dish={dish}
                        menuType={sec.type}
                        onAdd={addToCart}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ══════ CART SIDE ══════ */}
      <div className={`to-cart-side ${showCart ? 'to-cart-visible' : ''}`}>

        <div className="to-cart-hd">
          <div className="to-cart-hd-left">
            <ShoppingCart size={17} />
            <span>Order</span>
            {cartSize > 0 && <span className="to-cart-cnt">{cartSize}</span>}
          </div>
          <button className="to-cart-x" onClick={() => setShowCart(false)}>
            <ChevronDown size={20} />
          </button>
        </div>

        {/* ORDER TYPE SELECTOR */}
        <div className="to-order-type">
          <button
            className={`to-type-btn ${orderType === 'takeaway' ? 'to-type-on' : ''}`}
            onClick={() => setOrderType('takeaway')}
          >
            <Package size={14} />
            <span>Takeaway</span>
          </button>
          <button
            className={`to-type-btn ${orderType === 'dineIn' ? 'to-type-on' : ''}`}
            onClick={() => setOrderType('dineIn')}
          >
            <Store size={14} />
            <span>Dine In</span>
          </button>
        </div>

        {/* TABLE SELECTOR */}
        {orderType === 'dineIn' && (
          <div className="to-table-sel">
            {loadingTables ? (
              <div className="to-table-load">
                <div className="to-table-spin" />
                <span>Loading tables…</span>
              </div>
            ) : tables.length === 0 ? (
              <div className="to-table-empty">
                <p>No available tables</p>
                <span>All tables are occupied</span>
              </div>
            ) : (
              <>
                <label className="to-table-lbl">Select Table</label>
                <div className="to-table-grid">
                  {tables.map(table => (
                    <button
                      key={table._id}
                      className={`to-table-btn ${selectedTable?._id === table._id ? 'to-table-on' : ''}`}
                      onClick={() => setSelectedTable(table)}
                    >
                      <span className="to-table-num">{table.tableNumber}</span>
                      <span className="to-table-cap">{table.capacity} seats</span>
                      {table.areaId?.name && (
                        <span className="to-table-area">{table.areaId.name}</span>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* CUSTOMER FIELDS */}
        <div className="to-cust">
          {/* Phone first — searching by phone drives the name suggestion */}
          <div className="to-field-row to-field-row-relative">
            <Phone size={13} className="to-fi" />
            <input
              className="to-fi-in"
              placeholder="Phone (10 digits) *"
              type="tel"
              maxLength="10"
              value={custPhone}
              onChange={e => setCustPhone(e.target.value.replace(/\D/g, ''))}
              onFocus={() => { if (custSuggestions.length) setShowCustSuggestions(true); }}
              onBlur={() => setTimeout(() => setShowCustSuggestions(false), 120)}
            />
            {showCustSuggestions && (
              <CustomerSuggestions
                items={custSuggestions}
                loading={searchingCustomer}
                onPick={pickCustomerSuggestion}
              />
            )}
          </div>
          <div className="to-field-row">
            <User size={13} className="to-fi" />
            <input
              className="to-fi-in"
              placeholder="Customer name *"
              value={custName}
              onChange={e => setCustName(e.target.value)}
            />
          </div>
          <textarea
            className="to-note"
            placeholder="Special instructions…"
            rows={2}
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        </div>

        {/* ITEMS */}
        <div className="to-cart-list">
          {!hasCart ? (
            <div className="to-cart-nil">
              <ShoppingCart size={32} />
              <p>Cart is empty</p>
              <span>Tap a size button to add a dish</span>
            </div>
          ) : (
            Object.entries(cart).map(([key, item]) => (
              <CartRow key={key} item={item} onInc={inc} onDec={dec} onRemove={remove} />
            ))
          )}
        </div>

        {/* TOTALS */}
        {hasCart && (
          <div className="to-totals">
            {totals.hasMixed ? (
              /* Mixed cart — show split rows */
              <>
                <div className="to-trow">
                  <span>Taxable items</span><span>₹{totals.taxableSub}</span>
                </div>
                <div className="to-trow">
                  <span>MRP items <span className="to-trow-note">(tax incl.)</span></span>
                  <span>₹{totals.nonTaxableSub}</span>
                </div>
                {totals.totalTaxAmount > 0 && (
                  <div className="to-trow to-tax-row">
                    <span className="to-tax-row-label">
                      Total Tax <span className="to-trow-note">(on taxable)</span>
                      {totals.taxBreakdown.length > 0 && (
                        <button
                          type="button"
                          className="to-tax-toggle"
                          onClick={() => setShowTaxBreakdown(v => !v)}
                          aria-expanded={showTaxBreakdown}
                        >
                          <Receipt size={10} />
                          {showTaxBreakdown ? 'Hide' : 'View'} breakup
                          {showTaxBreakdown ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                        </button>
                      )}
                    </span>
                    <span>₹{totals.totalTaxAmount}</span>
                  </div>
                )}
              </>
            ) : totals.hasNonTaxable ? (
              /* All MRP — no tax line */
              <>
                <div className="to-trow">
                  <span>Subtotal</span><span>₹{totals.sub}</span>
                </div>
                <div className="to-trow to-trow-info">
                  <span>All prices MRP inclusive — no extra tax</span>
                </div>
              </>
            ) : (
              /* All taxable */
              <>
                <div className="to-trow"><span>Subtotal</span><span>₹{totals.sub}</span></div>
                {totals.totalTaxAmount > 0 && (
                  <div className="to-trow to-tax-row">
                    <span className="to-tax-row-label">
                      Total Tax
                      {totals.taxBreakdown.length > 0 && (
                        <button
                          type="button"
                          className="to-tax-toggle"
                          onClick={() => setShowTaxBreakdown(v => !v)}
                          aria-expanded={showTaxBreakdown}
                        >
                          <Receipt size={10} />
                          {showTaxBreakdown ? 'Hide' : 'View'} breakup
                          {showTaxBreakdown ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                        </button>
                      )}
                    </span>
                    <span>₹{totals.totalTaxAmount}</span>
                  </div>
                )}
              </>
            )}

            {showTaxBreakdown && totals.taxBreakdown.length > 0 && (
              <div className="to-tax-breakdown">
                {totals.taxBreakdown.map((t, idx) => (
                  <div key={`${t.name}-${idx}`} className="to-tax-bd-row">
                    <span className="to-tax-bd-name">
                      {t.name}
                      <span className="to-tax-bd-pct">({t.percentage}%)</span>
                    </span>
                    <span className="to-tax-bd-amt">₹{t.amount}</span>
                  </div>
                ))}
              </div>
            )}

            {totals.saved > 0 && (
              <div className="to-trow to-saved"><span>You save</span><span>₹{totals.saved}</span></div>
            )}
            <div className="to-trow to-grand"><span>Total</span><span>₹{totals.total}</span></div>
          </div>
        )}

        {/* CTA */}
        {success ? (
          <div className="to-ok">
            <Check size={16} />
            Order #{success.id.slice(-6).toUpperCase()} placed · ₹{success.total}
          </div>
        ) : (
          <button
            className="to-place"
            onClick={placeOrder}
            disabled={placing || !hasCart}
          >
            {placing
              ? <><div className="to-place-spin" />Processing…</>
              : <><Check size={15} />Place Order{hasCart ? ` · ₹${totals.total}` : ''}</>
            }
          </button>
        )}
      </div>

      {/* mobile overlay */}
      {showCart && (
        <div className="to-overlay" onClick={() => setShowCart(false)} />
      )}
    </div>
  );
};

export default TakeOrder;